/* ===========================================================
   Lucraa — Páginas legais (Termos de Uso + Política de Privacidade)
   Lançamento em waitlist (pré-produção). Textos em PT-BR, tema escuro
   premium, coluna de leitura legível. Registrado em docs/producao-v1.md (F10).
   =========================================================== */
import type { ReactNode } from "react";
import { Logo } from "../../components/ui.jsx";
import { useSeo } from "../../seo/use-seo";
import { titulo } from "../../seo/site";

/** Dados do controlador — centralizados aqui. */
const EMPRESA = {
  marca: "Lucraa+",
  controlador: "Digitalizee Tecnologia LTDA",
  // TODO: incluir o CNPJ na identificação do controlador (Termos + Privacidade)
  // antes do lançamento público — combinado com o time, pendente.
  endereco: "Rua Cel. José Eusébio, 95, Casa 13 — Higienópolis, São Paulo/SP, CEP 01239-030",
  email: "contato@digitalizee.com.br",
  encarregado: "Digitalizee Tecnologia LTDA",
  emailEncarregado: "contato@digitalizee.com.br",
  site: "lucraamais.com.br",
  atualizadoEm: "26 de junho de 2026",
};

function LegalShell({
  titulo,
  onVoltar,
  children,
}: {
  titulo: string;
  onVoltar: () => void;
  children: ReactNode;
}) {
  return (
    <div className="lp-root">
      <div className="lp-hero-bg" style={{ minHeight: "100vh" }}>
        <header className="lp-topnav">
          <div
            className="lp-wrap"
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              height: 70,
            }}
          >
            <button
              onClick={onVoltar}
              aria-label="Voltar para a página inicial"
              style={{ border: 0, background: "none", cursor: "pointer", padding: 0 }}
            >
              <Logo size={32} color="#fff" />
            </button>
            <button className="btn btn-ghost btn-sm" onClick={onVoltar}>
              Voltar
            </button>
          </div>
        </header>

        <main className="lp-wrap legal-doc" aria-labelledby="legal-h1">
          <h1 id="legal-h1">{titulo}</h1>
          <p className="legal-data">Última atualização: {EMPRESA.atualizadoEm}</p>
          {children}
        </main>
      </div>
    </div>
  );
}

/* ============================ TERMOS DE USO ============================ */

export function TermosDeUso({ onVoltar }: { onVoltar: () => void }) {
  useSeo({
    title: titulo("Termos de Uso"),
    description:
      "Termos de Uso do Lucraa+ — condições de acesso ao site e à lista de espera do analytics de lucro real para lojas Nuvemshop.",
    path: "/termos",
  });
  return (
    <LegalShell titulo="Termos de Uso" onVoltar={onVoltar}>
      <p>
        Estes Termos de Uso regem o acesso e o uso do site {EMPRESA.site} e da lista de espera do{" "}
        {EMPRESA.marca} ("Serviço"), operado por {EMPRESA.controlador}, com sede em {EMPRESA.endereco}.
        Ao se cadastrar na lista de espera ou navegar pelo site, você concorda com estes Termos. Se
        não concordar, não utilize o Serviço.
      </p>

      <h2>1. O que é o {EMPRESA.marca}</h2>
      <p>
        O {EMPRESA.marca} é uma plataforma de análise de lucro real para lojas Nuvemshop que
        investem em tráfego pago. No momento, o produto encontra-se em <strong>pré-lançamento</strong>{" "}
        e o acesso é liberado em lotes para quem entra na lista de espera. O cadastro na lista{" "}
        <strong>não garante</strong> acesso imediato, disponibilidade contínua nem preço futuro — as
        condições serão confirmadas no convite de acesso.
      </p>

      <h2>2. Lista de espera</h2>
      <p>
        Para entrar na lista de espera, você fornece dados de contato (nome, e-mail, telefone/WhatsApp,
        endereço da loja e faixa de faturamento). Esses dados são tratados conforme a nossa{" "}
        <a href="/privacidade">Política de Privacidade</a>. Você é responsável pela veracidade das
        informações fornecidas.
      </p>

      <h2>3. Uso permitido</h2>
      <p>
        Você concorda em usar o Serviço apenas para fins lícitos e em conformidade com estes Termos.
        É vedado: (i) tentar acessar áreas restritas sem autorização; (ii) interferir na segurança ou
        no funcionamento do site; (iii) copiar, reproduzir ou explorar comercialmente o conteúdo sem
        autorização; (iv) usar dados de terceiros sem consentimento ao se cadastrar.
      </p>

      <h2>4. Conteúdo ilustrativo</h2>
      <p>
        Os números, gráficos, depoimentos e a "loja de exemplo" exibidos no site são{" "}
        <strong>ilustrativos</strong>, baseados em uma loja fictícia, e servem apenas para demonstrar
        o funcionamento do produto. Não representam resultado garantido nem desempenho real de
        clientes, e não devem ser interpretados como promessa de retorno.
      </p>

      <h2>5. Propriedade intelectual</h2>
      <p>
        A marca {EMPRESA.marca}, o site, o software, os textos, o design e os demais elementos são de
        titularidade de {EMPRESA.controlador} ou licenciados a ela, protegidos pela legislação de
        propriedade intelectual. Nenhum direito é transferido a você pelo uso do Serviço.
      </p>

      <h2>6. Isenção de garantias e limitação de responsabilidade</h2>
      <p>
        O Serviço é oferecido "no estado em que se encontra", sem garantias de disponibilidade
        ininterrupta ou ausência de erros, especialmente por se tratar de fase de pré-lançamento. Na
        máxima extensão permitida pela lei, {EMPRESA.controlador} não se responsabiliza por decisões
        comerciais tomadas com base em informações do site nem por danos indiretos decorrentes do uso.
      </p>

      <h2>7. Comunicações</h2>
      <p>
        Ao se cadastrar, você concorda em receber comunicações sobre o lançamento, o convite de acesso
        e novidades do {EMPRESA.marca} por e-mail e/ou WhatsApp. Você pode cancelar o recebimento a
        qualquer momento pelos canais indicados nas mensagens ou em {EMPRESA.email}.
      </p>

      <h2>8. Alterações destes Termos</h2>
      <p>
        Podemos atualizar estes Termos a qualquer momento. A versão vigente estará sempre disponível
        nesta página, com a data da última atualização. O uso continuado do Serviço após mudanças
        significa concordância com a versão revisada.
      </p>

      <h2>9. Lei aplicável e foro</h2>
      <p>
        Estes Termos são regidos pelas leis da República Federativa do Brasil. Fica eleito o foro da
        comarca da sede da {EMPRESA.controlador} para dirimir eventuais controvérsias, com renúncia a
        qualquer outro, por mais privilegiado que seja.
      </p>

      <h2>10. Contato</h2>
      <p>
        Dúvidas sobre estes Termos podem ser enviadas para{" "}
        <a href={`mailto:${EMPRESA.email}`}>{EMPRESA.email}</a>.
      </p>
    </LegalShell>
  );
}

/* ===================== POLÍTICA DE PRIVACIDADE ===================== */

export function PoliticaPrivacidade({ onVoltar }: { onVoltar: () => void }) {
  useSeo({
    title: titulo("Política de Privacidade"),
    description:
      "Política de Privacidade do Lucraa+ — como a Digitalizee trata seus dados na lista de espera, em conformidade com a LGPD.",
    path: "/privacidade",
  });
  return (
    <LegalShell titulo="Política de Privacidade" onVoltar={onVoltar}>
      <p>
        Esta Política descreve como a {EMPRESA.controlador} trata os dados pessoais coletados no site{" "}
        {EMPRESA.site} e na lista de espera do {EMPRESA.marca}, em conformidade com a Lei nº
        13.709/2018 (Lei Geral de Proteção de Dados — LGPD).
      </p>

      <h2>1. Controlador dos dados</h2>
      <p>
        O controlador é a {EMPRESA.controlador}, com sede em {EMPRESA.endereco}. Contato:{" "}
        <a href={`mailto:${EMPRESA.email}`}>{EMPRESA.email}</a>.
      </p>

      <h2>2. Quais dados coletamos</h2>
      <ul>
        <li>
          <strong>Dados que você fornece</strong> ao entrar na lista de espera: nome, e-mail,
          telefone/WhatsApp, endereço da loja (URL) e faixa de faturamento.
        </li>
        <li>
          <strong>Dados de navegação</strong> coletados automaticamente: endereço IP, tipo de
          dispositivo e navegador, páginas visitadas e origem do tráfego (parâmetros de campanha/UTM),
          por meio de cookies e tecnologias similares.
        </li>
      </ul>

      <h2>3. Para que usamos os dados</h2>
      <ul>
        <li>Gerenciar a lista de espera e liberar o acesso em lotes;</li>
        <li>Comunicar o lançamento, enviar o convite de acesso e dar suporte;</li>
        <li>Entrar em contato comercial sobre o {EMPRESA.marca};</li>
        <li>Entender o uso do site e melhorar o produto e a comunicação;</li>
        <li>Cumprir obrigações legais e regulatórias.</li>
      </ul>

      <h2>4. Bases legais</h2>
      <p>
        O tratamento se apoia, conforme o caso, no <strong>consentimento</strong> do titular (art. 7º,
        I da LGPD) ao se cadastrar e aceitar comunicações, e no <strong>legítimo interesse</strong>{" "}
        (art. 7º, IX) para medição de audiência, segurança e melhoria do Serviço, sempre respeitando
        seus direitos e expectativas.
      </p>

      <h2>5. Compartilhamento de dados</h2>
      <p>
        Não vendemos seus dados. Podemos compartilhá-los com fornecedores que nos ajudam a operar o
        Serviço — provedores de hospedagem, e-mail, mensageria (WhatsApp), CRM e análise de uso (Mixpanel) —, que tratam os dados
        apenas sob nossas instruções e com obrigações de confidencialidade e segurança. Também podemos
        compartilhar dados para cumprir obrigação legal ou ordem de autoridade competente.
      </p>

      <h2>6. Cookies, análise de uso e tecnologias similares</h2>
      <p>
        Usamos armazenamento local essencial ao funcionamento do site e a ferramenta de análise de
        uso <strong>Mixpanel</strong> (Mixpanel, Inc.) para <strong>medição de audiência</strong> —
        entender de forma agregada como a página é navegada e melhorar o produto. Essa medição se
        apoia no <strong>legítimo interesse</strong> (art. 7º, IX da LGPD), com os dados de navegação
        associados a um identificador anônimo. Respeitamos o sinal <strong>"Não rastrear" (Do Not
        Track)</strong> do navegador; você também pode se opor limpando os dados deste site ou
        bloqueando cookies/armazenamento nas configurações do navegador — o que pode afetar
        funcionalidades do site.
      </p>

      <h2>7. Transferência internacional</h2>
      <p>
        Alguns fornecedores podem processar dados fora do Brasil — por exemplo, a ferramenta de
        análise <strong>Mixpanel</strong> processa dados nos <strong>Estados Unidos</strong>. Nesses
        casos, adotamos as garantias previstas na LGPD (art. 33) para assegurar nível de proteção
        adequado aos seus dados.
      </p>

      <h2>8. Por quanto tempo guardamos</h2>
      <p>
        Mantemos os dados da lista de espera enquanto durar a finalidade (pré-lançamento e
        relacionamento comercial) ou até você solicitar a exclusão. Dados de navegação são mantidos
        pelos prazos técnicos e legais aplicáveis. Após o prazo, os dados são eliminados ou
        anonimizados.
      </p>

      <h2>9. Segurança</h2>
      <p>
        Adotamos medidas técnicas e administrativas para proteger seus dados contra acessos não
        autorizados, perda, alteração e divulgação indevida. Nenhum sistema é 100% seguro, mas
        trabalhamos continuamente para reduzir riscos.
      </p>

      <h2>10. Seus direitos</h2>
      <p>Nos termos da LGPD (art. 18), você pode, a qualquer momento:</p>
      <ul>
        <li>confirmar a existência de tratamento e acessar seus dados;</li>
        <li>corrigir dados incompletos, inexatos ou desatualizados;</li>
        <li>solicitar anonimização, bloqueio ou eliminação de dados desnecessários;</li>
        <li>solicitar a portabilidade dos dados;</li>
        <li>revogar o consentimento e solicitar a exclusão dos dados tratados com base nele;</li>
        <li>obter informação sobre o compartilhamento dos seus dados.</li>
      </ul>
      <p>
        Para exercer seus direitos, escreva para{" "}
        <a href={`mailto:${EMPRESA.emailEncarregado}`}>{EMPRESA.emailEncarregado}</a>.
      </p>

      <h2>11. Dados de menores</h2>
      <p>
        O Serviço é destinado a lojistas maiores de 18 anos. Não coletamos intencionalmente dados de
        menores de idade. Caso identifique tratamento indevido, entre em contato para a remoção.
      </p>

      <h2>12. Encarregado (DPO)</h2>
      <p>
        Encarregado pelo tratamento de dados: {EMPRESA.encarregado}. Contato:{" "}
        <a href={`mailto:${EMPRESA.emailEncarregado}`}>{EMPRESA.emailEncarregado}</a>.
      </p>

      <h2>13. Alterações desta Política</h2>
      <p>
        Esta Política pode ser atualizada. A versão vigente, com a data da última atualização, estará
        sempre disponível nesta página.
      </p>
    </LegalShell>
  );
}
